import { useState, useEffect, useCallback } from "react";
import { invoicesAPI } from "../../data/api/invoicesApi";
import { InvoiceMapper } from "../../data/mappers/InvoiceMapper";
import { InvoiceEntity, InvoiceListStatus } from "../../domain/entities/InvoiceEntity";

export type TabType = "all" | "posted" | "pending" | "rejected" | "duplicate";

interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  current_page: number;
  total_pages: number;
}

interface UseAllInvoicesReturn {
  documents: InvoiceEntity[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  stats: {
    posted: number;
    pending: number;
    rejected: number;
    duplicate: number;
  };
  fetchInvoicesByTab: (
    tab: TabType,
    page: number,
    limit: number,
    sortBy?: "created_at" | "updated_at",
    sortOrder?: "asc" | "desc"
  ) => Promise<void>;
}

const getStatusForTab = (tab: TabType): InvoiceListStatus => {
  switch (tab) {
    case "posted":
      return "posted";
    case "pending":
      return "pending";
    case "rejected":
      return "rejected";
    case "duplicate":
      return "duplicate";
    case "all":
    default:
      return "all";
  }
};

export const useAllInvoices = (): UseAllInvoicesReturn => {
  const [documents, setDocuments] = useState<InvoiceEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [stats, setStats] = useState({
    posted: 0,
    pending: 0,
    rejected: 0,
    duplicate: 0,
  });


  /**
   * Fetch invoices based on active tab with server-side pagination and sorting
   */
  const fetchInvoicesByTab = useCallback(
    async (
      tab: TabType,
      page: number = 1,
      limit: number = 10,
      sortBy: "created_at" | "updated_at" = "created_at",
      sortOrder: "asc" | "desc" = "desc"
    ): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        const offset = (page - 1) * limit;
        const response = await invoicesAPI.fetchInvoices({
          limit,
          offset,
          status: getStatusForTab(tab),
          sort_by: sortBy,
          sort_order: sortOrder,
        });

        const result = InvoiceMapper.toInvoicesResultEntity(response);
        setDocuments(result.invoices);

        // Calculate pagination info
        const total = result.pagination?.total || 0;
        const totalPages = result.pagination?.total_pages ?? Math.ceil(total / limit);
        setPagination({
          total,
          limit: result.pagination?.limit ?? limit,
          offset: result.pagination?.offset ?? offset,
          has_more: result.pagination?.has_more ?? offset + limit < total,
          current_page: result.pagination?.current_page ?? page,
          total_pages: totalPages,
        });
      } catch (err) {
        console.error("Error fetching invoices by tab:", err);
        setError("Failed to fetch invoices");
        setDocuments([]);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Fetch stats for all tabs
   */
  const fetchStats = useCallback(async (): Promise<void> => {
    try {
      const counts = await invoicesAPI.fetchInvoiceCounts();
      setStats({
        posted: counts.posted_count || 0,
        pending: counts.pending_count || 0,
        rejected: counts.rejected_count || 0,
        duplicate: counts.duplicate_count || 0,
      });
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  }, []);

  // Fetch stats on mount
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    documents,
    loading,
    error,
    pagination,
    stats,
    fetchInvoicesByTab,
  };
};
